'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/language'

const EmbeddingSpace = dynamic(() => import('../_components/widgets/three/EmbeddingSpace'), { ssr: false })

export default function Embeddings() {
  return (
    <Lesson>
      <Step gate="q-onehot">
        <H>The problem with ID numbers</H>
        <P>
          A token ID is just a label. Token 412 isn’t “closer” to token 413 than to token 9000. If we fed raw IDs to a network,
          it would think <em>cat</em> (412) and <em>cathedral</em> (413) were nearly the same. The naive fix, a{' '}
          <strong>one-hot</strong> vector (all zeros except a 1 in the token’s slot), treats every word as exactly equally
          different from every other.
        </P>
        <Quiz
          id="q-onehot"
          question="With one-hot vectors, what is the dot product between “cat” and “kitten”?"
          options={[
            { label: '0 — every pair of different words is perpendicular', correct: true },
            { label: 'Close to 1, since they’re related', why: 'One-hot vectors know nothing about meaning: different words share no non-zero slot.' },
            { label: '−1', why: 'All entries are 0 or 1, so the dot product can’t be negative.' },
          ]}
          explain="One-hot encodings carry no notion of similarity. Whatever the model learns about ‘cat’ can’t transfer to ‘kitten’."
        />
      </Step>

      <Step>
        <H>Embeddings: meaning as position</H>
        <P>
          An <strong>embedding</strong> gives every token a short list of numbers — a vector, a point in space — and lets
          training decide where each point goes. Words used in similar ways end up near each other. Directions in the space
          start to carry meaning.
        </P>
        <Figure n="8.3" wide caption="Word embeddings in 3D (our toy vectors, squashed from 14 dimensions). Drag to orbit. Then try the analogy: the arrow from ‘man’ to ‘king’ is almost the same as the arrow from ‘woman’ to ‘queen’.">
          <EmbeddingSpace />
        </Figure>
        <P>Because meaning has become geometry, you can do arithmetic with it:</P>
        <MB>{'\\vec{\\text{king}} - \\vec{\\text{man}} + \\vec{\\text{woman}} \\approx \\vec{\\text{queen}}'}</MB>
        <Callout kind="note">
          We built these vectors by hand from features like “royal” and “male”, so you can see why the geometry works. In a real
          model nobody chooses the features: the embeddings start random and are learned by gradient descent, and the directions
          that emerge are rarely so neatly labelled.
        </Callout>
      </Step>

      <Step gate="analogy">
        <H>Do the arithmetic</H>
        <Exercise
          id="analogy"
          title="Nearest neighbours and analogies"
          {...EX.analogy}
          hints={[
            <>Loop over <C>Object.keys(E)</C>, skip excluded words, keep the word with the highest <C>vec.cosine(v, E[w])</C>.</>,
            <>
              <C>vec.add(vec.sub(E[a], E[b]), E[c])</C>, then <C>nearest(v, [a, b, c])</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-table">
        <H>An embedding is just a table</H>
        <P>
          Mechanically, an embedding is a matrix with one row per token. “Embedding token 7” means “take row 7”. The rows are
          parameters like any weight: the loss’s gradient flows back into exactly the rows that were used, nudging them.
        </P>
        <MB>{'\\text{embed}(\\text{token}_i) = E[i,\\,:] \\qquad E \\in \\mathbb{R}^{|V| \\times d}'}</MB>
        <Callout kind="notation">
          <M>{'\\mathbb{R}^{|V| \\times d}'}</M> means “a matrix of real numbers with |V| rows (one per vocabulary item) and d
          columns (the embedding size)”. GPT-3’s embedding table has about 50,000 rows of 12,288 numbers each.
        </Callout>
        <Quiz
          id="q-table"
          question="During training, which embedding rows get updated on a given step?"
          options={[
            { label: 'Only the rows for tokens that appeared in that step’s batch', correct: true },
            { label: 'Every row, every step', why: 'Rows that weren’t used had no effect on the loss, so their gradient is zero.' },
            { label: 'None — embeddings are fixed', why: 'They’re learned parameters, updated by backprop like any weight.' },
          ]}
        />
      </Step>
    </Lesson>
  )
}
